import React, { useCallback, useEffect } from 'react';

import { Plugins } from '@capacitor/core';

import { useLocationContext } from 'state/location';
import { usePypestreamContext } from 'state/pypestream';
import { isNativeIOS } from 'utils/environment';
import noop from 'utils/noop';
import { routes } from 'utils/routing';

import useAuth from '../../hooks/auth/use-auth';

import { Placeholder } from './styled';

const { Keyboard } = Plugins;

export const onKeyboardShow = ({ keyboardHeight }: { keyboardHeight: number }) =>
  requestAnimationFrame(() => {
    const pypestreamWrapper = document.getElementById('pypestream-wrapper');
    if (!!pypestreamWrapper) {
      pypestreamWrapper.style.transform = `translateY(-${keyboardHeight - 100}px)`;
    }
  });

export const onKeyboardHide = () =>
  requestAnimationFrame(() => {
    const pypestreamWrapper = document.getElementById('pypestream-wrapper');
    if (!!pypestreamWrapper) {
      pypestreamWrapper.style.transform = `translateY(0px)`;
    }
  });

export const Pypestream = () => {
  const { show, hide, injectScript } = usePypestreamContext();
  const { isAuthenticated, setOriginLocation, originLocation } = useAuth();
  const {
    location: { pathname },
    navigate,
  } = useLocationContext();
  const isLoggedIn = isAuthenticated();

  useEffect(() => {
    if (isLoggedIn) {
      setOriginLocation(null);
    } else if (pathname !== originLocation) {
      // user is not logged in,
      // this makes it so that signing in will redirect the user back to this page
      setOriginLocation(pathname);
    }
  }, [isLoggedIn, originLocation, pathname, setOriginLocation]);

  useEffect(() => injectScript(), [injectScript]);

  useEffect(() => {
    show();
    return () => {
      hide();
    };
  }, [hide, show]);

  useEffect(() => {
    if (isNativeIOS()) {
      Keyboard.addListener('keyboardWillShow', onKeyboardShow);
      Keyboard.addListener('keyboardWillHide', onKeyboardHide);
      return () => {
        Keyboard.removeAllListeners();
      };
    }
    return noop;
  }, []);

  // Pypestream calls `window.top.postMessage("REDIRECT_TO_LOGIN", "*")`
  // from within its iframe when the user clicks a "Log In" button in the chat UI
  const listenForMessage = useCallback(
    event => {
      if (
        !isLoggedIn &&
        event.origin?.endsWith('pypestream.com') &&
        event.data === 'REDIRECT_TO_LOGIN'
      ) {
        navigate(routes.signIn);
      }
    },
    [isLoggedIn, navigate]
  );

  useEffect(() => {
    window.addEventListener('message', listenForMessage, false);
    return () => {
      window.removeEventListener('message', listenForMessage);
    };
  });

  return <Placeholder />;
};
